(() => {
  const cfg = window.archiveWishlist;
  if (!cfg?.proxyBase) return;

  const PROXY = cfg.proxyBase.replace(/\/$/, '');
  const PENDING_ADD_PARAM = 'wishlist_add';
  const DEBUG = cfg.debug === true;
  const inFlight = new Map();

  function wlLog(...args) {
    if (DEBUG) console.log('[archiveWishlist]', ...args);
  }

  function wlError(context, detail) {
    console.error('[archiveWishlist]', context, detail);
  }

  function proxyUrl(path, params = {}) {
    const url = new URL(`${PROXY}/${path}`, window.location.origin);
    Object.entries(params).forEach(([k, v]) => {
      if (v != null && v !== '') url.searchParams.set(k, String(v));
    });
    return url.toString();
  }

  function userFacingError(detail) {
    const code = detail?.code;
    const err = typeof detail?.error === 'string' ? detail.error.trim() : '';
    const status = detail?.status;

    if (code === 'PROXY_AUTH') {
      return 'Wishlist app needs to be opened once in Shopify admin (Apps → archive-wishlist), then refresh this page.';
    }
    if (status === 401 || /sign in required/i.test(err)) {
      return 'Please sign in to use your wishlist.';
    }
    if (/variant not found|unavailable/i.test(err)) {
      return 'This product variant is unavailable.';
    }
    if (err) return err;
    if (status === 404) return 'Wishlist service not found. Redeploy the archive-wishlist app on Vercel.';
    if (status >= 500) return 'Wishlist is temporarily unavailable. Try again in a moment.';
    return wishlistString('error');
  }

  async function proxyFetch(path, options = {}) {
    const { params = {}, method = 'GET', body, headers: extraHeaders, ...rest } = options;
    const url = proxyUrl(path, params);
    wlLog('fetch', method, url);

    const res = await fetch(url, {
      method,
      credentials: 'same-origin',
      ...rest,
      headers: {
        Accept: 'application/json',
        ...(extraHeaders || {}),
      },
      ...(body != null ? { body } : {}),
    });

    const contentType = res.headers.get('content-type') || '';
    let data = {};
    if (contentType.includes('application/json')) {
      data = await res.json().catch(() => ({}));
    } else {
      const text = await res.text().catch(() => '');
      wlError('non-json response', { path, status: res.status, preview: text.slice(0, 200) });
    }

    if (!res.ok) {
      const detail = {
        path,
        status: res.status,
        code: data.code,
        error: data.error || res.statusText,
        url,
      };
      wlError('proxy failed', detail);
      const err = new Error(userFacingError(detail));
      err.wishlistDetail = detail;
      throw err;
    }

    wlLog('ok', path, data);
    return data;
  }

  function dedupe(key, fn) {
    if (inFlight.has(key)) return inFlight.get(key);
    const promise = fn().finally(() => inFlight.delete(key));
    inFlight.set(key, promise);
    return promise;
  }

  const celebrate = window.archiveCelebrate;
  const reducedMotion = celebrate?.reducedMotion ?? window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const FALLBACK_STRINGS = {
    added: 'Added to wishlist',
    removed: 'Removed from wishlist',
    loginToManage: 'Sign in to save and manage your wishlist.',
    error: 'Something went wrong. Please try again.',
    remove: 'Remove from wishlist',
  };

  function isMissingTranslation(value) {
    return typeof value === 'string' && /translation missing/i.test(value);
  }

  function wishlistString(key) {
    const value = cfg.strings?.[key];
    if (!value || isMissingTranslation(value)) return FALLBACK_STRINGS[key] || '';
    return value;
  }

  let lastToastMessage = '';
  let lastToastAt = 0;

  function showToast(message, { force = false } = {}) {
    const text = typeof message === 'string' && message && !isMissingTranslation(message)
      ? message
      : wishlistString('error');
    const now = Date.now();
    if (!force && text === lastToastMessage && now - lastToastAt < 1200) return;
    lastToastMessage = text;
    lastToastAt = now;

    let el = document.querySelector('[data-wishlist-toast]');
    if (!el) {
      el = document.createElement('div');
      el.className = 'wishlist-toast';
      el.setAttribute('data-wishlist-toast', '');
      el.setAttribute('role', 'status');
      el.setAttribute('aria-live', 'polite');
      document.body.appendChild(el);
    }

    window.clearTimeout(showToast._hide);
    window.clearTimeout(showToast._t);

    el.textContent = text;
    el.hidden = false;
    el.classList.remove('is-visible');

    requestAnimationFrame(() => {
      requestAnimationFrame(() => el.classList.add('is-visible'));
    });

    const hideDelay = reducedMotion ? 2200 : 2800;
    const fadeMs = reducedMotion ? 0 : 400;

    showToast._t = window.setTimeout(() => {
      el.classList.remove('is-visible');
      showToast._hide = window.setTimeout(() => {
        el.hidden = true;
      }, fadeMs);
    }, hideDelay);
  }

  function updateHeaderCount(count, opts) {
    const n = Math.max(0, Number(count) || 0);
    document.querySelectorAll('[data-wishlist-count]').forEach((badge) => {
      celebrate?.updateCountBadge(badge, n, opts);
    });
    document.querySelectorAll('.header__icon--wishlist').forEach((link) => {
      link.classList.toggle('has-items', n > 0);
      link.setAttribute('aria-label', n > 0 ? `Wishlist, ${n} item${n === 1 ? '' : 's'}` : 'Wishlist');
    });
  }

  function pulseWishlistButton(btn) {
    if (!btn || reducedMotion) return;
    btn.classList.remove('is-pulse');
    void btn.offsetWidth;
    btn.classList.add('is-pulse');
    btn.addEventListener('animationend', () => btn.classList.remove('is-pulse'), { once: true });
  }

  async function celebrateWishlistAdd(btn, count) {
    if (btn) {
      setButtonActive(btn, true);
      pulseWishlistButton(btn);
    }

    const link = document.querySelector('.header__icon--wishlist');
    if (!celebrate) {
      updateHeaderCount(count, { bump: true });
      return;
    }

    await celebrate.celebrate({
      originEl: btn,
      link,
      targetSelector: '.header__wishlist-heart--outline',
      count,
    });
  }

  async function refreshHeaderCount() {
    if (!cfg.loggedIn) return;
    try {
      const data = await dedupe('count', () => proxyFetch('count'));
      updateHeaderCount(data.count ?? 0);
    } catch (e) {
      wlError('header count', e?.wishlistDetail || e);
    }
  }

  function redirectToLogin(variantId) {
    const returnUrl = new URL(window.location.pathname + window.location.search, window.location.origin);
    if (variantId) returnUrl.searchParams.set(PENDING_ADD_PARAM, String(variantId));

    const login = new URL(cfg.loginUrl || '/account/login', window.location.origin);
    login.searchParams.set('return_url', returnUrl.pathname + returnUrl.search);
    window.location.href = login.toString();
  }

  function clearPendingAddFromUrl() {
    const url = new URL(window.location.href);
    if (!url.searchParams.has(PENDING_ADD_PARAM)) return;
    url.searchParams.delete(PENDING_ADD_PARAM);
    const next = url.pathname + (url.searchParams.toString() ? `?${url.searchParams}` : '') + url.hash;
    window.history.replaceState({}, '', next);
  }

  function setButtonActive(btn, active) {
    if (!btn) return;
    btn.classList.toggle('is-active', active);
    btn.setAttribute('aria-pressed', active ? 'true' : 'false');
  }

  async function checkStatus(btn, variantId) {
    if (!cfg.loggedIn || !btn || !Number.isFinite(variantId)) return;
    try {
      const data = await dedupe(`status:${variantId}`, () =>
        proxyFetch('status', { params: { variant_id: variantId } }),
      );
      setButtonActive(btn, !!data.in_wishlist);
    } catch (e) {
      wlError('status', e?.wishlistDetail || e);
    }
  }

  async function addVariant(variantId) {
    return dedupe(`add:${variantId}`, () =>
      proxyFetch('add', { params: { variant_id: variantId } }),
    );
  }

  async function removeVariant(variantId) {
    return dedupe(`remove:${variantId}`, () =>
      proxyFetch('remove', { params: { variant_id: variantId } }),
    );
  }

  async function onWishlistButtonClick(btn, event) {
    event?.preventDefault();
    event?.stopPropagation();

    const variantId = Number(btn.getAttribute('data-variant-id'));
    if (!Number.isFinite(variantId)) return;

    const isActive = btn.classList.contains('is-active');

    if (!cfg.loggedIn) {
      if (isActive) {
        redirectToLogin();
        return;
      }
      redirectToLogin(variantId);
      return;
    }

    try {
      btn.disabled = true;
      const data = isActive ? await removeVariant(variantId) : await addVariant(variantId);
      const nowActive = !isActive;
      if (nowActive) {
        await celebrateWishlistAdd(btn, data.count);
      } else {
        setButtonActive(btn, false);
        updateHeaderCount(data.count);
      }
      showToast(isActive ? wishlistString('removed') : wishlistString('added'), { force: true });
    } catch (e) {
      wlError('toggle wishlist', e?.wishlistDetail || e);
      showToast(e?.message || wishlistString('error'), { force: true });
    } finally {
      btn.disabled = false;
    }
  }

  function bindButtons() {
    document.querySelectorAll('[data-wishlist-btn]').forEach((btn) => {
      if (btn.dataset.wishlistBound === '1') return;
      btn.dataset.wishlistBound = '1';
      const variantId = Number(btn.getAttribute('data-variant-id'));
      checkStatus(btn, variantId);
      btn.addEventListener('click', (event) => onWishlistButtonClick(btn, event));
    });
  }

  function bindVariantSync() {
    const form = document.querySelector('form[data-type="add-to-cart-form"]');
    const syncFromInput = () => {
      const input = form?.querySelector('input[name="id"]');
      const vid = input?.value;
      if (!vid) return;
      document.querySelectorAll('[data-wishlist-btn]').forEach((btn) => {
        btn.setAttribute('data-variant-id', vid);
        setButtonActive(btn, false);
        checkStatus(btn, Number(vid));
      });
    };

    form?.querySelector('input[name="id"]')?.addEventListener('change', syncFromInput);

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
        const variant = event?.data?.variant;
        const vid = variant?.id;
        if (!vid) return;
        document.querySelectorAll('[data-wishlist-btn]').forEach((btn) => {
          btn.setAttribute('data-variant-id', String(vid));
          setButtonActive(btn, false);
          checkStatus(btn, Number(vid));
        });
      });
    }
  }

  async function processPendingWishlistAdd() {
    if (!cfg.loggedIn) return;

    const url = new URL(window.location.href);
    const variantId = Number(url.searchParams.get(PENDING_ADD_PARAM));
    if (!Number.isFinite(variantId)) return;

    clearPendingAddFromUrl();

    try {
      const data = await addVariant(variantId);
      showToast(wishlistString('added'), { force: true });
      const btn = document.querySelector(`[data-wishlist-btn][data-variant-id="${variantId}"]`);
      await celebrateWishlistAdd(btn, data.count);
    } catch (e) {
      wlError('pending add', e?.wishlistDetail || e);
    }
  }

  function escapeHtml(text) {
    return String(text)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function buildWishlistGridFromApi(items) {
    const ul = document.createElement('ul');
    ul.className =
      'grid product-grid contains-card contains-card--product wishlist-cards__grid';
    ul.setAttribute('data-wishlist-api-grid', '');
    ul.setAttribute('role', 'list');

    items.forEach((item, index) => {
      const li = document.createElement('li');
      li.className = 'grid__item wishlist-cards__item';
      li.dataset.variantId = String(item.variant_id);

      const imgHtml = item.image
        ? `<img src="${escapeHtml(item.image)}" alt="${escapeHtml(item.title || '')}" loading="lazy" width="533" height="666" class="motion-reduce">`
        : '<div class="media media--transparent"></div>';

      li.innerHTML = `
        <button type="button" class="wishlist-cards__remove" data-wishlist-remove="${item.variant_id}" aria-label="${escapeHtml(wishlistString('remove'))}">
          <span aria-hidden="true">×</span>
        </button>
        <div class="card-wrapper product-card-wrapper underline-links-hover">
          <div class="card card--media ratio" style="--ratio-percent: 125%;">
            <div class="card__inner color-scheme-1 gradient ratio">
              <div class="card__media">
                <div class="media media--transparent media--hover-effect">
                  <a href="${escapeHtml(item.url || '#')}" class="full-unstyled-link">${imgHtml}</a>
                </div>
              </div>
            </div>
            <div class="card__content">
              <div class="card__information">
                <h3 class="card__heading h5">
                  <a href="${escapeHtml(item.url || '#')}" class="full-unstyled-link">${escapeHtml(item.title || '')}</a>
                </h3>
                <div class="card-information">
                  ${item.price ? `<div class="price"><span class="price-item price-item--regular">${escapeHtml(item.price)}</span></div>` : ''}
                </div>
              </div>
            </div>
          </div>
        </div>`;

      if (index > 2) {
        li.querySelector('img')?.setAttribute('loading', 'lazy');
      }

      ul.appendChild(li);
    });

    return ul;
  }

  function bindWishlistRemoveButtons(scope, gridHost, empty) {
    scope.querySelectorAll('[data-wishlist-remove]').forEach((btn) => {
      if (btn.dataset.wishlistRemoveBound === '1') return;
      btn.dataset.wishlistRemoveBound = '1';
      btn.addEventListener('click', async (event) => {
        event.preventDefault();
        event.stopPropagation();
        const vid = Number(btn.getAttribute('data-wishlist-remove'));
        const item = btn.closest('.wishlist-cards__item');
        try {
          const res = await removeVariant(vid);
          updateHeaderCount(res.count);

          if (!item || reducedMotion) {
            item?.remove();
          } else {
            item.classList.add('is-removing');
            item.addEventListener('transitionend', () => item.remove(), { once: true });
            window.setTimeout(() => {
              if (item?.isConnected) item.remove();
            }, 320);
          }

          const grid = gridHost?.querySelector('[data-wishlist-cards-grid], [data-wishlist-grid] > ul, [data-wishlist-api-grid]');
          if (!grid?.querySelector('.wishlist-cards__item') && empty) empty.hidden = false;
          if (!grid?.querySelector('.wishlist-cards__item') && gridHost) gridHost.hidden = true;
        } catch (ex) {
          wlError('remove from page', ex?.wishlistDetail || ex);
          showToast(ex?.message || wishlistString('error'), { force: true });
        }
      });
    });
  }

  function initWishlistPage() {
    const root = document.querySelector('[data-wishlist-page]');
    if (!root) return;

    if (!cfg.loggedIn) return;

    const serverGrid = root.querySelector('[data-wishlist-server-grid]');
    const needsApi = root.querySelector('[data-wishlist-needs-api]');
    const empty = root.querySelector('[data-wishlist-empty]');
    const loading = root.querySelector('[data-wishlist-loading]');

    const bindGrid = (scope) => {
      const count = scope.querySelectorAll('.wishlist-cards__item').length;
      updateHeaderCount(count);
      bindWishlistRemoveButtons(scope, scope, empty);
      if (typeof window.initializeScrollAnimationTrigger === 'function') {
        window.initializeScrollAnimationTrigger(scope);
      }
    };

    if (serverGrid && serverGrid.querySelector('.wishlist-cards__item')) {
      if (loading) loading.hidden = true;
      if (needsApi) needsApi.hidden = true;
      bindGrid(root);
      return;
    }

    if (!needsApi) return;
    needsApi.hidden = false;

    proxyFetch('list')
      .then((data) => {
        if (loading) loading.hidden = true;
        const items = data.items || [];
        updateHeaderCount(data.count ?? items.length);

        if (items.length === 0) {
          if (empty) empty.hidden = false;
          return;
        }

        let mount = serverGrid?.parentElement;
        if (!mount) {
          mount = document.createElement('div');
          mount.className = 'wishlist-page__collection';
          const pageWidth = root.querySelector('.page-width');
          pageWidth?.appendChild(mount);
        }

        if (serverGrid) serverGrid.remove();

        try {
          const grid = buildWishlistGridFromApi(items);
          mount.appendChild(grid);
          bindGrid(root);
        } catch (e) {
          wlError('grid build', e);
          if (empty) {
            empty.textContent = wishlistString('error');
            empty.hidden = false;
          }
        }
      })
      .catch((ex) => {
        wlError('wishlist page load', ex?.wishlistDetail || ex);
        if (loading) loading.hidden = true;
        if (empty) {
          empty.textContent = ex?.message || wishlistString('error');
          empty.hidden = false;
        }
      });
  }

  function init() {
    bindButtons();
    bindVariantSync();
    refreshHeaderCount();
    processPendingWishlistAdd();
    initWishlistPage();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
